/**
 * Sentry Test Page
 * For verifying Sentry frontend integration
 * Access: /sentry-test (admin only in production)
 */

import React, { useState } from 'react';
import { 
  triggerTestError, 
  triggerTestException,
  WorkflowTracker,
  captureMessage,
  setUserContext,
  isSentryInitialized
} from '../utils/sentry';

export default function SentryTestPage() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const addResult = (test, result) => {
    setResults(prev => [...prev, { 
      test, 
      result, 
      timestamp: new Date().toISOString() 
    }]);
  };
  
  const runFrontendTests = async () => {
    setLoading(true);
    setResults([]);
    
    // Test 1: Check initialization
    const initialized = isSentryInitialized();
    addResult('Sentry Initialized', initialized ? '✅ Yes' : '❌ No');
    
    if (!initialized) {
      setLoading(false);
      return;
    }
    
    // Test 2: Send test message
    const msgResult = triggerTestError();
    addResult('Test Message', msgResult.success ? `✅ ID: ${msgResult.messageId}` : `❌ ${msgResult.reason}`);
    
    // Test 3: Send test exception
    const excResult = triggerTestException();
    addResult('Test Exception', excResult.success ? `✅ ID: ${excResult.eventId}` : `❌ ${excResult.reason}`);
    
    // Test 4: Workflow tracking
    try {
      WorkflowTracker.authStart('test');
      WorkflowTracker.authSuccess('test-user-id', 'test');
      addResult('Workflow Tracking', '✅ Auth workflow tracked');
    } catch (e) {
      addResult('Workflow Tracking', `❌ ${e.message}`);
    }
    
    // Test 5: User context
    try {
      setUserContext(
        { id: 'test-user', email: 'test@slabreality.com' },
        { role_system: 'admin', full_name: 'Test User' }
      );
      addResult('User Context', '✅ User context set');
    } catch (e) {
      addResult('User Context', `❌ ${e.message}`);
    }
    
    // Test 6: Custom message
    try {
      captureMessage('Frontend test completed', 'info', { 
        tags: { test: 'true' } 
      });
      addResult('Custom Message', '✅ Sent');
    } catch (e) {
      addResult('Custom Message', `❌ ${e.message}`);
    }
    
    setLoading(false);
  };
  
  const runBackendTests = async () => {
    setLoading(true);
    
    try {
      // Test backend status
      const statusRes = await fetch('/api/sentry-status');
      const status = await statusRes.json();
      addResult('Backend Status', status.initialized ? `✅ ${status.environment}` : '❌ Not initialized');
      
      // Test backend message
      const testRes = await fetch('/api/sentry-test');
      const test = await testRes.json();
      addResult('Backend Test Message', test.status === 'ok' ? `✅ ID: ${test.event_id}` : '❌ Failed');
      
      // Test backend error
      const errorRes = await fetch('/api/sentry-error-test');
      const error = await errorRes.json();
      addResult('Backend Error Test', error.status === 'error_captured' ? '✅ Captured' : '❌ Failed');
      
    } catch (e) {
      addResult('Backend Tests', `❌ ${e.message}`);
    }
    
    setLoading(false);
  };
  
  return (
    <div className="min-h-screen bg-[#050505] text-white p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-light mb-2">Sentry Integration Test</h1>
        <p className="text-zinc-400 mb-8">Verify Sentry monitoring is working correctly</p>
        
        <div className="flex gap-4 mb-8">
          <button
            onClick={runFrontendTests}
            disabled={loading}
            className="px-6 py-3 bg-[#B08B5C] text-black font-medium rounded-lg hover:brightness-110 disabled:opacity-50"
          >
            {loading ? 'Testing...' : 'Test Frontend'}
          </button>
          
          <button
            onClick={runBackendTests}
            disabled={loading}
            className="px-6 py-3 bg-zinc-800 text-white font-medium rounded-lg hover:bg-zinc-700 disabled:opacity-50"
          >
            {loading ? 'Testing...' : 'Test Backend'}
          </button>
          
          <button
            onClick={() => setResults([])}
            className="px-6 py-3 bg-zinc-900 text-zinc-400 font-medium rounded-lg hover:bg-zinc-800"
          >
            Clear
          </button>
        </div>
        
        {results.length > 0 && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-zinc-800">
              <h2 className="text-lg font-medium">Test Results</h2>
            </div>
            <div className="divide-y divide-zinc-800">
              {results.map((r, i) => (
                <div key={i} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{r.test}</div>
                    <div className="text-xs text-zinc-500">{r.timestamp}</div>
                  </div>
                  <div className="text-sm font-mono">{r.result}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="mt-8 p-4 bg-zinc-900/30 border border-zinc-800 rounded-lg">
          <h3 className="text-sm font-medium text-zinc-400 mb-2">Instructions</h3>
          <ol className="text-sm text-zinc-500 space-y-1 list-decimal list-inside">
            <li>Click "Test Frontend" to send test events from React</li>
            <li>Click "Test Backend" to send test events from FastAPI</li>
            <li>Go to Sentry dashboard to verify events appear</li>
            <li>Check for Event IDs in results above</li>
          </ol>
        </div>
        
        <div className="mt-4 text-xs text-zinc-600">
          Environment: {process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development'} | 
          Version: {process.env.REACT_APP_VERSION || 'unknown'}
        </div>
      </div>
    </div>
  );
}
